// Wire validation only. No regex execution, filesystem access, approval or HTML.
// A structurally valid pack still requires a reviewed digest and source custody.
export const ESP_DIALECT_ROLES = Object.freeze(['unsubscribe', 'preference_center', 'view_in_browser', 'personalization', 'tracking', 'link', 'image', 'editable_region', 'module', 'head']);
export const ESP_SYSTEM_ROLES = Object.freeze(ESP_DIALECT_ROLES.slice(0, 3));
const SHA = /^[a-f0-9]{64}$/;
const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const plain = v => !!v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;
const keys = (v, expected) => plain(v) && Object.keys(v).length === expected.length && expected.every(k => Object.hasOwn(v, k));
const utf8 = new TextEncoder();
const text = (v, max = 16000) => typeof v === 'string' && v.length > 0 && !v.includes('\0') && utf8.encode(v).length <= max
  && new TextDecoder('utf-8', { fatal: true }).decode(utf8.encode(v)) === v;
const texts = (v, max = 128) => Array.isArray(v) && v.length <= max && v.every(x => text(x)) && new Set(v).size === v.length;
const fail = (code = 'esp_dialect_pack_invalid') => { throw Object.assign(new Error(code), { code, status: 422 }); };
const compareKeys = (a, b) => {
  const x = [...a], y = [...b];
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i].codePointAt(0) - y[i].codePointAt(0);
  return x.length - y.length;
};
export function canonicalEspDialect(value) {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalEspDialect).join(',') + ']';
  if (!plain(value)) fail(); // Wire format deliberately has no numeric fields.
  return '{' + Object.keys(value).sort(compareKeys).map(k => JSON.stringify(k) + ':' + canonicalEspDialect(value[k])).join(',') + '}';
}
export function validateEspRoleEvidence(v) {
  if (!keys(v, ['schema', 'role_order', 'roles', 'negative', 'tokens', 'tag_roles']) || v.schema !== 'EspRoleEvidence.v1'
    || JSON.stringify(v.role_order) !== JSON.stringify(ESP_SYSTEM_ROLES) || !keys(v.roles, ESP_SYSTEM_ROLES)) fail();
  for (const row of Object.values(v.roles)) {
    if (!keys(row, ['text', 'href']) || !['text', 'href'].every(k => texts(row[k]) && row[k].length)) fail();
  }
  if (!texts(v.negative) || !v.negative.length || !Array.isArray(v.tokens) || v.tokens.length !== 3) fail();
  v.tokens.forEach((r, i) => { if (!keys(r, ['pattern', 'role']) || r.role !== ESP_SYSTEM_ROLES[i] || !text(r.pattern)) fail(); });
  if (!keys(v.tag_roles, ['unsubscribe', 'preferences', 'webversion']) || v.tag_roles.unsubscribe !== 'unsubscribe'
    || v.tag_roles.preferences !== 'preference_center' || v.tag_roles.webversion !== 'view_in_browser') fail();
  return v;
}
export function validateEspDialectPack(p) {
  if (!keys(p, ['schema', 'esp', 'esp_key', 'version', 'profile_digest', 'pack_digest', 'dialect_regex', 'token_grammar',
    'unsupported_dialect_patterns', 'forbidden', 'roles', 'role_evidence', 'additional_tags', 'additional_tags_semantics', 'esp_tier_shape', 'provenance'])
    || p.schema !== 'EspDialectPack.v1') fail();
  if (!['esp', 'esp_key'].every(k => typeof p[k] === 'string' && SLUG.test(p[k]))
    || !['version', 'dialect_regex', 'token_grammar'].every(k => text(p[k]))
    || !['profile_digest', 'pack_digest'].every(k => typeof p[k] === 'string' && SHA.test(p[k]))) fail();
  if (!texts(p.unsupported_dialect_patterns) || !plain(p.forbidden) || Object.keys(p.forbidden).length > 64
    || Object.entries(p.forbidden).some(([k, v]) => !SLUG.test(k) || ['__proto__', 'prototype', 'constructor'].includes(k) || !texts(v))) fail();
  if (!plain(p.roles) || !ESP_SYSTEM_ROLES.every(r => Object.hasOwn(p.roles, r)) || Object.keys(p.roles).some(r => !ESP_DIALECT_ROLES.includes(r))) fail();
  for (const row of Object.values(p.roles)) {
    if (!keys(row, ['token', 'in_template', 'placement', 'authoring_rule', 'evidence'])
      || !(row.in_template === null || typeof row.in_template === 'boolean')
      || !['token', 'placement', 'authoring_rule'].every(k => row[k] === null || text(row[k]))
      || !texts(row.evidence) || row.token !== null && !row.evidence.length) fail();
  }
  validateEspRoleEvidence(p.role_evidence);
  if (p.additional_tags_semantics !== 'allowed_recipes_not_instructions' || !Array.isArray(p.additional_tags) || p.additional_tags.length > 128) fail();
  const seen = new Set();
  for (const row of p.additional_tags) {
    if (!keys(row, ['token', 'role', 'placement_rule']) || !ESP_DIALECT_ROLES.includes(row.role) || !text(row.token, 4000) || !text(row.placement_rule, 4000)) fail();
    const id = row.role + '\0' + row.token; if (seen.has(id)) fail(); seen.add(id);
  }
  if (!['basic_advanced', 'untiered', 'unverified'].includes(p.esp_tier_shape)) fail();
  const v = p.provenance;
  if (!keys(v, ['grounded', 'self_derived', 'exemplar_sha256']) || typeof v.grounded !== 'boolean'
    || !(v.self_derived === null || typeof v.self_derived === 'boolean') || !plain(v.exemplar_sha256) || Object.keys(v.exemplar_sha256).length > 128) fail();
  for (const [path, digest] of Object.entries(v.exemplar_sha256)) {
    if (!text(path, 512) || !path.startsWith('exemplars/') || path.includes('\\') || path.split('/').some(part => ['', '.', '..'].includes(part))
      || typeof digest !== 'string' || !SHA.test(digest)) fail();
  }
  if (v.grounded && (v.self_derived !== false || !Object.keys(v.exemplar_sha256).length)) fail('esp_dialect_grounding_unproven');
  // Compute the digest in the Node/Python authority loader. A browser shape
  // check cannot authorize this pack or elevate self-declared provenance.
  return p;
}
export function espDialectDigestInput(pack) {
  validateEspDialectPack(pack);
  return canonicalEspDialect(Object.fromEntries(Object.entries(pack).filter(([k]) => k !== 'pack_digest')));
}
